import { useSurfaceProps } from '../surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoAnnularRingWeldLine,
  GeckoComponentCoords,
  GeckoComponentModelType,
  GeckoComponentPath,
} from 'types'

import { FeatureLabel3D } from './procedural/FeatureLabel3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from './procedural/SurfaceLineFeature3D'

/**
 * Renders an annular ring weld line: a radial line at the model's angle (degrees,
 * counterclockwise from the floor's positive x-axis), spanning from the innermost
 * annular ring to the floor border. Renders nothing when the floor has no
 * positioned annular ring.
 */
export const AnnularRingWeldLineFeature3D: React.FC<{
  model: GeckoAnnularRingWeldLine
}> = ({ model }) => {
  const { bounds, componentModel } = useSurfaceProps()

  const { path, labelCoords } = React.useMemo(() => {
    const floor =
      componentModel?.type === GeckoComponentModelType.TankFloor
        ? componentModel
        : undefined
    const innerRadius = floor?.insideAnnularRingsRadius?.convertTo(
      bounds.x.unit
    ).num
    const outerRadius = floor?.radius.convertTo(bounds.x.unit).num
    if (
      innerRadius === undefined ||
      outerRadius === undefined ||
      innerRadius >= outerRadius
    ) {
      return { path: undefined, labelCoords: undefined }
    }

    const centerX = (bounds.x.start.num + bounds.x.end.num) / 2
    const centerY = (bounds.y.start.num + bounds.y.end.num) / 2

    const angleRadians = (model.angle * Math.PI) / 180
    const cos = Math.cos(angleRadians)
    const sin = Math.sin(angleRadians)

    const path = GeckoComponentPath.CreateFromValues(
      componentModel?.id ?? '',
      [
        { x: centerX + innerRadius * cos, y: centerY + innerRadius * sin },
        { x: centerX + outerRadius * cos, y: centerY + outerRadius * sin },
      ],
      bounds.x.unit,
      bounds.y.unit
    )

    const midRadius = (innerRadius + outerRadius) / 2
    const labelCoords = GeckoComponentCoords.CreateFromValues(
      componentModel?.id ?? '',
      centerX + midRadius * cos,
      bounds.x.unit,
      centerY + midRadius * sin,
      bounds.y.unit
    )

    return { path, labelCoords }
  }, [bounds, componentModel, model.angle])

  return !path || !labelCoords ? null : (
    <group name={model.id}>
      <SurfaceLineFeature3D
        path={path}
        featureModel={model}
        lineSelectionType={SurfaceLineSelectionType.FeatureLine}
      />
      <FeatureLabel3D feature={model} coords={labelCoords} anchor='top left' />
    </group>
  )
}
